'use client';

import { useMemo, useRef } from 'react';

import { useGetUrlWithAnonymousId } from '@flipster/features/src/shared/hooks/useGetUrlWithAnonymousId';
import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { zendesk } from '@flipster/features/src/shared/zendesk/url';
import { useZendesk } from '@flipster/features/src/shared/zendesk/use-zendesk';
import { Button, TextButton } from '@flipster/react/design-system';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { Styles, css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { maxWStylesWithMargin, sectionLargeHeadingStyles } from '@web/app/[locale]/(web)/_main-page/_styles';
import { useMainPageIntersectionSegment } from '@web/app/[locale]/(web)/_main-page/hooks/useIntersectionSegment';
import { YoutubeIcon } from '@web/assets/svgs/main-page';

import Collapsible from './Collapsible';

const Faq = ({ css: cssProp }: { css?: Styles }) => {
  const intersectionRef = useRef<HTMLHeadingElement>(null);
  useMainPageIntersectionSegment(intersectionRef, 'Frequently asked questions');
  const { openMessenger } = useZendesk();
  const isSpotOnly = useCheckSpotOnly();
  const getUrlWithAnonymousId = useGetUrlWithAnonymousId();
  const Contents = useMemo(
    () => [
      {
        title: 'What is Flipster?',
        desc: (
          <>
            <strong>
              Flipster is a high-performance crypto perpetuals trading platform engineered by traders, optimized for
              trading.
            </strong>
            <br />
            <br />
            We deliver <strong>zero-friction trading:</strong> zero spreads on major perp pairs, ultra-fast execution,
            and precision order fills the moment you click. It’s a level of performance and cost-efficiency rarely seen,
            built for those who demand more from every trade.
            <br />
            <br />
            <strong>Earn while you trade</strong> — passively earn yield on your USDT balance even as you stay active in
            the market. No missed opportunities. Just full-spectrum capital efficiency.
          </>
        ),
      },
      {
        title: 'How to sign up on Flipster?',
        desc: (
          <>
            {'You can '}
            <IsomorphicLink href='/signup' newTab>
              register
            </IsomorphicLink>
            {' for a Flipster account in three easy steps:'}
            <br />
            <br />
            {'1. Go to the '}
            <IsomorphicLink href='/'>Flipster website</IsomorphicLink>
            {' or download the Flipster app ('}
            <IsomorphicLink href={getUrlWithAnonymousId('WebFaq')} newTab>
              iOS
            </IsomorphicLink>
            {' or '}
            <IsomorphicLink href={getUrlWithAnonymousId('AndroidFaq')} newTab>
              Android
            </IsomorphicLink>
            ).
            <br />
            <br />
            {'2. '}
            <IsomorphicLink href='/signup' newTab>
              Sign up
            </IsomorphicLink>
            {' for an account on the Flipster website or within the Flipster app.'}
            <br />
            <br />
            {'3. Complete '}
            <IsomorphicLink href={zendesk.getUrl('HowToVerifyYourFlipsterAccount')} newTab>
              identity verification
            </IsomorphicLink>
            <br />
            <br />
            <strong>Note:</strong> We recommend that you check your{' '}
            <IsomorphicLink href={zendesk.getUrl('RestrictedCountries')} newTab>
              country's eligibility
            </IsomorphicLink>
            {
              ' before you start, as eligibility can change over time. Be sure to visit the eligibility page in the future to see if your country has been removed from the restriction list.'
            }
            <br />
            <br />
            {
              'You must complete your identity verification to unlock all the features in your Flipster account, such as trading, deposit and withdrawal. If you encounter any issues during the process, you can contact customer support by '
            }
            <IsomorphicLink href={zendesk.getUrl('RequestNew')} newTab>
              submitting an enquiry
            </IsomorphicLink>
            , or via our 24/7 live chat.
          </>
        ),
        youtube: (
          <>
            <TextButton
              color='accent'
              size='large'
              as='a'
              href='https://www.youtube.com/watch?v=OVtG2aA1emI'
              newTab
              icon={<YoutubeIcon />}
              label='How to Sign Up on Flipster | Step by Step Guide'
            />
            <br />
            <TextButton
              color='accent'
              size='large'
              as='a'
              href='https://www.youtube.com/watch?v=0JOvJJOldzk'
              newTab
              icon={<YoutubeIcon />}
              label='How to Complete Identity Verification (KYC) on Flipster | Step by Step Guide'
            />
          </>
        ),
      },
      {
        title: 'How to earn crypto rewards on Flipster?',
        desc: (
          <>
            {'Flipster supports its community during all '}
            <IsomorphicLink href='/blog/4-stages-crypto-market-cycle' newTab>
              market cycles
            </IsomorphicLink>
            {
              ' by offering various initiatives that enhance the trading experience. Users can explore numerous opportunities to participate in our events, promotions, and trading competitions, and gain rewards through our '
            }
            <IsomorphicLink href='/marketing/rewards-hub' newTab>
              Rewards Hub
            </IsomorphicLink>
            {', '}
            <IsomorphicLink href='/marketing/referral' newTab>
              Referral
            </IsomorphicLink>
            {', and '}
            <IsomorphicLink href='/marketing/vip' newTab>
              VIP
            </IsomorphicLink>
            {' Programs.'}
            <br />
            <br />
            <span className={css({ fontWeight: 'bold', color: 'content.default.level-4' })}>
              Trade and earn simultaneously
            </span>
            <br />
            <br />
            {'To support our users in maximizing their funds during a bear market, we introduced the '}
            <IsomorphicLink href='/earn' newTab>
              Earn Campaign
            </IsomorphicLink>
            {'. This campaign allows traders to earn passive rewards with a high '}
            <IsomorphicLink href='/glossary/annual-percentage-rate-apr' newTab>
              APR
            </IsomorphicLink>
            {' on the USDT, BTC, and ETH wallet balance in their Flipster accounts.'}
            <br />
            <br />
            {
              'A key advantage is users can earn APR rewards on their USDT balance while actively trading derivatives at the same time, all from the same wallet. As they strategize on their next move, users can be ready to act quickly when market opportunities arise, avoiding delays from making '
            }
            <IsomorphicLink href='/support/guide/perpetual-swaps' newTab>
              swaps
            </IsomorphicLink>
            , depositing fresh funds, or transferring crypto.
          </>
        ),
      },
      {
        title: "What are Flipster's trading fees?",
        desc: (
          <>
            {'Flipster uses a '}
            <IsomorphicLink href='/support/guide/fees' newTab>
              tiered trading fee system
            </IsomorphicLink>
            {' with ultra-low, transparent trading fees — the more you trade, the more you save.'}
            <br />
            <br />
            Starting from just 0.025%, our tiered fee structure rewards high-volume traders with even lower rates.
            Whether you’re scalping or swinging, every trade benefits from deeper liquidity, tighter spreads, and
            near-zero slippage — so you keep more of what you earn.
            <br />
            <br />
            With zero-spread on 20+ major perpetual pairs, you get cleaner entries, sharper exits, and better execution
            — even in fast-moving markets. There’s no guesswork, no hidden costs — just precision pricing that moves
            with you.
          </>
        ),
      },
      {
        title: 'Why choose Flipster to trade altcoin derivatives?',
        desc: (
          <>
            Flipster offers a structural edge for altcoin derivative traders by minimizing hidden costs and optimizing
            execution:
            <ul>
              <li>
                <strong>Zero spreads on 20+ major perp pairs:</strong> Unlike most platforms that quote separate bid/ask
                prices, Flipster provides a single, unified price, removing spread costs that can quietly erode returns,
                especially on volatile or less liquid altcoins.
              </li>
              <li>
                <IsomorphicLink href='blog/how-flipster-stacks-up-real-trading-costs-across-top-perps-exchanges' newTab>
                  Lowest all-in trading costs:
                </IsomorphicLink>
                {' While some exchanges may post lower fees at top-tier VIP levels, these are often offset by larger'}
                spreads and slippage. Flipster’s zero-friction execution model delivers lower total cost per trade
                across all tiers and sizes.
              </li>
              <li>
                <strong>Transparent, predictable pricing:</strong> With tier-based fee discounts and flat, visible
                rates, traders benefit from a clear cost curve that scales with volume, without execution surprises.
              </li>
            </ul>
            This matters most when trading altcoins like major altcoins perps, where spread and slippage can often
            exceed the posted fee. Flipster is designed to keep those costs low and consistent, so traders can operate
            with precision.
          </>
        ),
      },
    ],
    [getUrlWithAnonymousId],
  );

  const SpotOnlyContents = useMemo(
    () => [
      {
        title: 'What is Flipster?',
        desc: (
          <>
            Flipster is the leading cryptocurrency trading platform. Traders rely on our app to gain a competitive edge,
            turning their insights and knowledge into potential profits. Discover a new way to trade cryptocurrencies
            and join a thriving community of traders who return to our platform time and again.
          </>
        ),
      },
      {
        title: 'How has Flipster optimized the trading experience?',
        desc: (
          <>
            Flipster offers an optimized user experience across both mobile and web platforms, featuring seamless
            cross-platform integration. This allows users to easily trade cryptos and analyze charts on the web while
            staying updated on the go through the mobile app (
            <IsomorphicLink href={getUrlWithAnonymousId('WebFaq')} newTab>
              iOS
            </IsomorphicLink>
            {' or '}
            <IsomorphicLink href={getUrlWithAnonymousId('AndroidFaq')} newTab>
              Android
            </IsomorphicLink>
            ).
            <br />
            <br />
            Recognizing that today's traders aren't tied to their desktops, we've redesigned our trading platform to be
            user-friendly and intuitive, moving away from the complex interfaces of traditional financial platforms.
            <br />
            <br />
            Flipster's app and website are built for speed and execution, ensuring users can enjoy fast, frictionless
            trades, track portfolios, and take advantage of market opportunities—anytime, anywhere.
          </>
        ),
      },
      {
        title: 'How to get started on Flipster?',
        desc: (
          <>
            {'You can '}
            <IsomorphicLink href='/support/guide/registration' newTab>
              register
            </IsomorphicLink>
            {' for a Flipster account in three easy steps.'}
            <br />
            {'1. Go to the '}
            <IsomorphicLink href='/'>Flipster website</IsomorphicLink>
            {' or download the Flipster app '}(
            <IsomorphicLink href={getUrlWithAnonymousId('WebFaq')} newTab>
              iOS
            </IsomorphicLink>
            {' or '}
            <IsomorphicLink href={getUrlWithAnonymousId('AndroidFaq')} newTab>
              Android
            </IsomorphicLink>
            ).
            <br />
            {'2. '}
            <IsomorphicLink href='/signup' newTab>
              Sign up
            </IsomorphicLink>
            {' for an account on the Flipster website or within the Flipster app.'}
            <br />
            {'3. Complete '}
            <IsomorphicLink href={zendesk.getUrl('HowToVerifyYourFlipsterAccount')} newTab>
              identity verification
            </IsomorphicLink>
            .
            <br />
            <br />
            Note for UAE residents: Flipster is committed to providing the safest and most seamless trading experience
            in accordance with the regulations set by the Virtual Assets Regulatory Authority (VARA). By aligning with
            local compliance standards, we ensure that UAE users can access our platform with confidence and ease.
            <br />
            <br />
            {
              'You must complete your identity verification to unlock all the features in your Flipster account, such as trading, deposit and withdrawal. If you encounter any issues during the process, you can contact customer support by '
            }
            <IsomorphicLink href={zendesk.getUrl('RequestNew')} newTab>
              submitting an enquiry
            </IsomorphicLink>
            , or via our 24/7 live chat.
          </>
        ),
        youtube: (
          <TextButton
            color='accent'
            size='large'
            as='a'
            href='https://www.youtube.com/watch?v=OVtG2aA1emI'
            newTab
            icon={<YoutubeIcon />}
            label='How to Sign Up for a Flipster Account?'
          />
        ),
      },
      {
        title: 'How to register an account on the Flipster app?',
        desc: (
          <>
            {'1. Download the Flipster app (iOS or Android) . You can find us in the '}
            <IsomorphicLink href={getUrlWithAnonymousId('AndroidFaq')} newTab>
              Google Play
            </IsomorphicLink>
            {' or '}
            <IsomorphicLink href={getUrlWithAnonymousId('WebFaq')} newTab>
              Apple App Store
            </IsomorphicLink>
            .
            <br />
            <br />
            2. Sign up with your email address, Google account, Apple ID or via Social sign-on (SSO).
            <br />
            <br />
            3. Complete identity verification.
          </>
        ),
        youtube: (
          <TextButton
            color='accent'
            size='large'
            as='a'
            href='https://www.youtube.com/watch?v=0JOvJJOldzk'
            newTab
            icon={<YoutubeIcon />}
            label='How to Complete Identity Verification on Flipster?'
          />
        ),
      },
      {
        title: 'How to trade crypto on Flipster?',
        desc: (
          <>
            1. On the mobile application, go to the [Assets] page. Click on [Receive crypto] to deposit cryptocurrencies
            to your Flipster account from an external wallet.
            <br />
            <br />
            2. Under the Trade tab, select an asset you wish to trade. Next, enter the amount of asset you wish to buy
            or sell before clicking [Review your order].
            <br />
            <br />
            3. Check all the details before tapping buy or sell.
          </>
        ),
        youtube: (
          <TextButton
            color='accent'
            size='large'
            as='a'
            href='https://www.youtube.com/watch?v=SKm9i7AVIYw'
            newTab
            icon={<YoutubeIcon />}
            label='Trade on Flipster'
          />
        ),
      },
      {
        title: 'How to deposit crypto to Flipster?',
        desc: (
          <>
            {`1. On the mobile app, click on [Asset], followed by [Deposit crypto]. Alternatively, if you're on the website,
            go to the [Wallet] menu and select [Receive Crypto].`}
            <br />
            <br />
            {'2. Select the cryptocurrency asset you wish to '}
            <IsomorphicLink href='/blog/how-to-deposit-crypto-to-flipster' newTab>
              deposit
            </IsomorphicLink>
            , and the deposit network. A network and a wallet address will be generated.
            <br />
            <br />
            3. Scan the QR code or copy and paste the wallet address manually using your crypto wallet or external
            platform, to send crypto to your Flipster wallet.
            <br />
            <br />
            Note:
            <br />
            {'- Flipster does not charge any deposit '}
            <IsomorphicLink href='/support/guide/fees' newTab>
              fees
            </IsomorphicLink>
            , but you may incur network fees when transferring funds from an external wallet to your Flipster wallet.
            <br />
            <br />
            {
              "- If you select an incompatible or different deposit network, you might lose your funds. Please check the contract address of the asset you're depositing, which must be the same as the one displayed, to ensure your "
            }
            <IsomorphicLink href='/support/guide/assets' newTab>
              assets
            </IsomorphicLink>
            {' get deposited.'}
            <br />
            <br />
            {`- Transactions can take some time to be confirmed, depending on the network traffic. Be patient, you'll see the
            funds in your Flipster account shortly. We'll also send you an email once the funds have been deposited on
            Flipster.`}
            <br />
            <br />- You may monitor the deposit status by going to the [History] - [Deposit] page.
          </>
        ),
        youtube: (
          <>
            <TextButton
              color='accent'
              size='large'
              as='a'
              href='https://www.youtube.com/watch?v=JhqmnI-8lqE'
              newTab
              icon={<YoutubeIcon />}
              label='How to Deposit on the Flipster App'
            />
            <br />
            <TextButton
              color='accent'
              size='large'
              as='a'
              href='https://www.youtube.com/watch?v=0WsA0BOCF4E'
              newTab
              icon={<YoutubeIcon />}
              label='How to Deposit Crypto on the Flipster Web'
            />
          </>
        ),
      },
    ],
    [getUrlWithAnonymousId],
  );

  const faqContents = useMemo(
    () => (isSpotOnly ? SpotOnlyContents : Contents),
    [Contents, SpotOnlyContents, isSpotOnly],
  );

  return (
    <section className={css(maxWStylesWithMargin, cssProp)}>
      <h2 ref={intersectionRef} className={css(sectionLargeHeadingStyles, { mb: { base: 32, tablet: 40 } })}>
        Frequently asked questions
      </h2>
      <div
        className={css({ borderTop: '1px solid', borderColor: 'border.default.level-4', mb: { base: 32, tablet: 40 } })}
      >
        {faqContents.map((content, index) => (
          <Collapsible key={content.title} index={index} title={content.title}>
            <div
              className={css({
                textStyle: 'Text/L/Regular',
                color: 'content.default.level-2',
                '& a': { textStyle: 'Text/L/Underline', color: 'content.accent.info' },
                '& strong': {
                  textStyle: 'Text/L/Strong',
                },
                '& ul li': {
                  textStyle: 'Text/L/Regular',
                  display: 'list-item',
                  listStyleType: 'disc',
                  ml: 16,
                  my: 10,
                },
              })}
            >
              {content.desc}
            </div>
            {content.youtube && <div className={css({ mt: 22 })}>{content.youtube}</div>}
          </Collapsible>
        ))}
      </div>

      <div className={center()}>
        <Button
          hierarchy='primary'
          color='accent'
          size='medium'
          label='More questions?'
          onClick={() => {
            segment.track(MainEvent.MainPageChatClick);
            openMessenger();
          }}
          css={{ minW: 154 }}
        />
      </div>
    </section>
  );
};

export default Faq;
